import React, { useState } from 'react';
import {
  DollarSign,
  TrendingUp,
  PieChart as PieIcon,
  Vote,
  MessageSquare,
  Plus,
  Calendar,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  Send,
  Users,
  Shield,
  Briefcase,
  FileSpreadsheet,
  Award,
  ChevronRight,
  Landmark,
  Layers,
  Sparkles,
  X,
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  AreaChart,
  Area,
} from 'recharts';
import { BoardCategory, BoardDiscussion, BoardStatus, FinancialMonthMetric } from '../types';
import { useApp } from '../context/AppContext';

export const BoardAndReporting: React.FC = () => {
  const {
    boardDiscussions,
    financialMetrics,
    addBoardDiscussion,
    castBoardVote,
    addBoardComment,
    currentUser,
    bills,
  } = useApp();

  const [activeSubTab, setActiveSubTab] = useState<'financials' | 'board_decisions'>('financials');
  const [boardCategoryFilter, setBoardCategoryFilter] = useState<'all' | BoardCategory>('all');
  const [showNewProposalModal, setShowNewProposalModal] = useState(false);
  const [selectedDiscussion, setSelectedDiscussion] = useState<BoardDiscussion | null>(null);

  // Voting form inside selected discussion
  const [voteComment, setVoteComment] = useState('');
  const [commentInput, setCommentInput] = useState('');

  // New proposal form
  const [proposalTitle, setProposalTitle] = useState('');
  const [proposalCategory, setProposalCategory] = useState<BoardCategory>('security_rule');
  const [proposalBudget, setProposalBudget] = useState('');
  const [proposalDeadline, setProposalDeadline] = useState('2026-08-30');
  const [proposalDescription, setProposalDescription] = useState('');

  // Key Financial KPIs
  const currentMonthMetric = financialMetrics[financialMetrics.length - 1];
  const totalOverdueBills = bills.filter((b) => b.status === 'overdue' || b.status === 'unpaid').reduce((sum, b) => sum + b.amount, 0);
  const totalPaidBills = bills.filter((b) => b.status === 'paid').reduce((sum, b) => sum + b.amount, 0);

  const filteredDiscussions = boardDiscussions.filter((d) => {
    return boardCategoryFilter === 'all' || d.category === boardCategoryFilter;
  });

  const getCategoryBadge = (cat: BoardCategory) => {
    switch (cat) {
      case 'finance_budget':
        return { label: 'งบประมาณ & การเงิน', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
      case 'maintenance_project':
        return { label: 'โครงการซ่อมบำรุง', color: 'bg-blue-50 text-blue-700 border-blue-200' };
      case 'security_rule':
        return { label: 'กฎระเบียบ & ความปลอดภัย', color: 'bg-purple-50 text-purple-700 border-purple-200' };
      case 'procurement':
        return { label: 'จัดซื้อจัดจ้าง', color: 'bg-amber-50 text-amber-700 border-amber-200' };
      case 'urgent_vote':
        return { label: 'วาระเร่งด่วน', color: 'bg-rose-50 text-rose-700 border-rose-200' };
    }
  };

  const getStatusBadge = (status: BoardStatus) => {
    switch (status) {
      case 'active_voting':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
            <Vote className="w-3 h-3 text-blue-600 animate-pulse" /> เปิดลงมติ (Active Voting)
          </span>
        );
      case 'passed':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle2 className="w-3 h-3 text-emerald-600" /> มีมติเห็นชอบ (Passed)
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
            <XCircle className="w-3 h-3 text-rose-600" /> มติไม่อนุมัติ (Rejected)
          </span>
        );
      case 'in_discussion':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
            <MessageSquare className="w-3 h-3 text-amber-600" /> อยู่ระหว่างรับฟังความเห็น
          </span>
        );
    }
  };

  const handleCreateProposal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!proposalTitle.trim() || !proposalDescription.trim()) return;

    await addBoardDiscussion({
      title: proposalTitle.trim(),
      category: proposalCategory,
      description: proposalDescription.trim(),
      budget_amount: proposalBudget ? parseFloat(proposalBudget) : undefined,
      created_by: currentUser?.full_name || 'คุณอนุกูล พรหมดี (กรรมการ)',
      author_role: currentUser?.role === 'admin' ? 'ฝ่ายบริหารนิติบุคคล' : 'กรรมการหมู่บ้าน',
      deadline: proposalDeadline,
      status: 'active_voting',
    });

    setShowNewProposalModal(false);
    setProposalTitle('');
    setProposalDescription('');
    setProposalBudget('');
  };

  const handleCastVote = async (vote: 'approve' | 'reject' | 'abstain') => {
    if (!selectedDiscussion) return;
    await castBoardVote(selectedDiscussion.id, vote, voteComment.trim() || undefined);
    setVoteComment('');
    // Refresh selected discussion state
    const updated = boardDiscussions.find((d) => d.id === selectedDiscussion.id);
    if (updated) setSelectedDiscussion(updated);
  };

  const handlePostComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDiscussion || !commentInput.trim()) return;
    await addBoardComment(selectedDiscussion.id, commentInput.trim());
    setCommentInput('');
    const updated = boardDiscussions.find((d) => d.id === selectedDiscussion.id);
    if (updated) setSelectedDiscussion(updated);
  };

  return (
    <div className="space-y-6">
      {/* Top Tab Switcher */}
      <div className="flex items-center justify-between bg-white p-2 rounded-2xl border border-slate-200 shadow-xs">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveSubTab('financials')}
            className={`px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 transition-all ${
              activeSubTab === 'financials'
                ? 'bg-blue-700 text-white shadow-sm shadow-blue-500/20'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            <TrendingUp className="w-4 h-4" /> ตัวชี้วัดทางการเงิน (Financial Metrics)
          </button>
          <button
            onClick={() => setActiveSubTab('board_decisions')}
            className={`px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 transition-all ${
              activeSubTab === 'board_decisions'
                ? 'bg-blue-700 text-white shadow-sm shadow-blue-500/20'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            <Vote className="w-4 h-4" /> กระดานตัดสินใจคณะกรรมการ (Board Decisions)
            {boardDiscussions.filter((d) => d.status === 'active_voting').length > 0 && (
              <span className="w-2 h-2 rounded-full bg-amber-400"></span>
            )}
          </button>
        </div>

        {activeSubTab === 'board_decisions' && (
          <button
            onClick={() => setShowNewProposalModal(true)}
            className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm shadow-emerald-600/20 transition-colors mr-1"
          >
            <Plus className="w-4 h-4" /> ยื่นญัตติ / วาระใหม่
          </button>
        )}
      </div>

      {/* VIEW 1: FINANCIAL METRICS */}
      {activeSubTab === 'financials' && (
        <div className="space-y-6">
          {/* Financial KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Collected */}
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex items-center justify-between">
              <div>
                <span className="text-xs font-medium text-slate-500 block">ยอดเก็บค่าส่วนกลางสะสม (ส.ค. 69)</span>
                <div className="text-2xl font-bold text-emerald-600 mt-1">
                  ฿{currentMonthMetric.total_collected.toLocaleString()}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  จากยอดเรียกเก็บทั้งหมด ฿{currentMonthMetric.total_billed.toLocaleString()}
                </div>
              </div>
              <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-200 flex items-center justify-center">
                <DollarSign className="w-5 h-5" />
              </div>
            </div>

            {/* Collection Rate */}
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex items-center justify-between">
              <div>
                <span className="text-xs font-medium text-slate-500 block">อัตราการจัดเก็บสำเร็จ (Collection Rate)</span>
                <div className="text-2xl font-bold text-blue-700 mt-1">
                  {currentMonthMetric.collection_rate_pct}%
                </div>
                <div className="mt-1.5 w-28 bg-slate-100 h-1.5 rounded-full overflow-hidden">
                  <div
                    className="bg-blue-600 h-full rounded-full"
                    style={{ width: `${currentMonthMetric.collection_rate_pct}%` }}
                  />
                </div>
              </div>
              <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 border border-blue-200 flex items-center justify-center">
                <TrendingUp className="w-5 h-5" />
              </div>
            </div>

            {/* Sinking Reserve Fund */}
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex items-center justify-between">
              <div>
                <span className="text-xs font-medium text-slate-500 block">เงินกองทุนสำรองส่วนกลาง (Reserve Fund)</span>
                <div className="text-2xl font-bold text-purple-700 mt-1">฿1,850,000</div>
                <div className="text-[11px] text-emerald-600 font-medium mt-0.5">สภาพคล่องแข็งแกร่ง (ฝากประจำ 12M)</div>
              </div>
              <div className="w-11 h-11 rounded-xl bg-purple-50 text-purple-600 border border-purple-200 flex items-center justify-center">
                <Landmark className="w-5 h-5" />
              </div>
            </div>

            {/* Pending & Overdue */}
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex items-center justify-between">
              <div>
                <span className="text-xs font-medium text-slate-500 block">ยอดค้างชำระรวม (Arrears)</span>
                <div className="text-2xl font-bold text-rose-600 mt-1">
                  ฿{currentMonthMetric.total_pending.toLocaleString()}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">ค้างชำระสะสม 4 หลังคาเรือน</div>
              </div>
              <div className="w-11 h-11 rounded-xl bg-rose-50 text-rose-600 border border-rose-200 flex items-center justify-center">
                <AlertCircle className="w-5 h-5" />
              </div>
            </div>
          </div>

          {/* Revenue vs Expense Chart & Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left 2 Cols: Monthly Financial Comparison Chart */}
            <div className="lg:col-span-2 bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-bold text-slate-800 text-sm">เปรียบเทียบรายรับ - รายจ่าย 6 เดือนย้อนหลัง</h4>
                  <p className="text-xs text-slate-500">ยอดเก็บค่าส่วนกลาง และค่าใช้จ่ายดำเนินงานส่วนกลาง</p>
                </div>
                <span className="px-2.5 py-1 rounded-lg text-xs bg-slate-100 text-slate-700 font-mono font-semibold">
                  ปีงบประมาณ 2569
                </span>
              </div>

              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={financialMetrics} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month_label" tick={{ fontSize: 11, fill: '#64748b' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                    <Tooltip
                      formatter={(val: number) => [`฿${val.toLocaleString()}`, '']}
                      contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '12px' }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar dataKey="total_collected" name="รายรับค่าส่วนกลาง" fill="#2563eb" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="operational_expense" name="ค่าใช้จ่ายดำเนินงาน" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="security_expense" name="ค่าจ้าง รปภ." fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Right 1 Col: Expense Structure Breakdown */}
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-4">
              <div>
                <h4 className="font-bold text-slate-800 text-sm">สัดส่วนค่าใช้จ่ายประจำเดือน (ส.ค. 69)</h4>
                <p className="text-xs text-slate-500">รวมค่าใช้จ่ายทั้งสิ้น ฿131,500 / เดือน</p>
              </div>

              <div className="space-y-3 pt-2 text-xs">
                <div>
                  <div className="flex justify-between mb-1 text-slate-700">
                    <span className="font-semibold">1. ค่าบริการรักษาความปลอดภัย (รปภ. 24 ชม.)</span>
                    <span className="font-bold">฿58,000 (44%)</span>
                  </div>
                  <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                    <div className="bg-blue-600 h-full rounded-full" style={{ width: '44%' }} />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-1 text-slate-700">
                    <span className="font-semibold">2. ค่าไฟฟ้าส่องสว่าง & น้ำประปาส่วนกลาง</span>
                    <span className="font-bold">฿36,000 (27%)</span>
                  </div>
                  <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                    <div className="bg-amber-500 h-full rounded-full" style={{ width: '27%' }} />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-1 text-slate-700">
                    <span className="font-semibold">3. พนักงานสวน & ทำความสะอาดสโมสร</span>
                    <span className="font-bold">฿29,000 (22%)</span>
                  </div>
                  <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                    <div className="bg-emerald-500 h-full rounded-full" style={{ width: '22%' }} />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-1 text-slate-700">
                    <span className="font-semibold">4. กองทุนซ่อมบำรุง & อะไหล่สระว่ายน้ำ</span>
                    <span className="font-bold">฿8,500 (7%)</span>
                  </div>
                  <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                    <div className="bg-purple-500 h-full rounded-full" style={{ width: '7%' }} />
                  </div>
                </div>
              </div>

              <div className="p-3 rounded-xl bg-blue-50/60 border border-blue-100 text-xs text-blue-900 mt-4">
                <span className="font-bold">สรุปผลประกอบการ:</span> มีกระแสเงินสดสุทธิส่วนเกิน +฿16,700 ยกยอดสะสมเข้าบัญชีกองทุนโครงการ
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VIEW 2: BOARD INTERNAL DECISION & RAPID VOTING */}
      {activeSubTab === 'board_decisions' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left 2 Cols: Board Proposals List */}
          <div className="lg:col-span-2 space-y-4">
            {/* Filter */}
            <div className="flex items-center gap-2 bg-white p-3 rounded-xl border border-slate-200 text-xs">
              <span className="text-slate-500">หมวดหมู่วาระ:</span>
              <select
                value={boardCategoryFilter}
                onChange={(e) => setBoardCategoryFilter(e.target.value as any)}
                className="bg-transparent font-medium text-slate-800 focus:outline-hidden cursor-pointer"
              >
                <option value="all">ทั้งหมด ({boardDiscussions.length} วาระ)</option>
                <option value="security_rule">กฎระเบียบ & ความปลอดภัย</option>
                <option value="maintenance_project">โครงการซ่อมบำรุง</option>
                <option value="finance_budget">งบประมาณ & การเงิน</option>
              </select>
            </div>

            {/* Proposal Cards */}
            <div className="space-y-3">
              {filteredDiscussions.map((item) => {
                const cat = getCategoryBadge(item.category);
                const approveVotes = item.votes.filter((v) => v.vote === 'approve').length;
                const totalVotes = item.votes.length;

                return (
                  <div
                    key={item.id}
                    onClick={() => setSelectedDiscussion(item)}
                    className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs hover:border-blue-400 cursor-pointer transition-all space-y-3"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold border ${cat.color}`}>
                          {cat.label}
                        </span>
                        {item.budget_amount && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-700 font-mono">
                            งบประมาณ ฿{item.budget_amount.toLocaleString()}
                          </span>
                        )}
                      </div>
                      {getStatusBadge(item.status)}
                    </div>

                    <h4 className="font-bold text-slate-800 text-sm leading-snug">{item.title}</h4>
                    <p className="text-xs text-slate-600 line-clamp-2">{item.description}</p>

                    {/* Voting Progress Bar */}
                    <div className="p-3 bg-slate-50 rounded-xl space-y-1.5 text-xs">
                      <div className="flex justify-between items-center text-[11px]">
                        <span className="text-slate-500 flex items-center gap-1 font-medium">
                          <Users className="w-3.5 h-3.5 text-blue-600" /> มติกรรมการ: {approveVotes} เห็นชอบ / {totalVotes} เสียงที่ลงคะแนน
                        </span>
                        <span className="text-slate-400">กำหนดปิดมติ: {item.deadline}</span>
                      </div>
                      <div className="w-full bg-slate-200 h-2 rounded-full overflow-hidden flex">
                        <div
                          className="bg-emerald-500 h-full"
                          style={{ width: `${(approveVotes / 7) * 100}%` }}
                          title={`เห็นชอบ ${approveVotes} เสียง`}
                        />
                        <div
                          className="bg-rose-500 h-full"
                          style={{ width: `${(item.votes.filter((v) => v.vote === 'reject').length / 7) * 100}%` }}
                          title="ไม่เห็นชอบ"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-[11px] pt-1 text-slate-400">
                      <span>ผู้เสนอ: {item.created_by}</span>
                      <span className="text-blue-600 font-semibold flex items-center gap-1">
                        ร่วมลงคะแนน & อภิปราย ({item.comments.length} ความเห็น) <ChevronRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right 1 Col: Selected Proposal Detailed Discussion & Vote Panel */}
          <div>
            {selectedDiscussion ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs space-y-4">
                <div className="flex items-start justify-between pb-3 border-b border-slate-100">
                  <div>
                    <span className="text-[10px] text-blue-700 font-bold uppercase tracking-wider">
                      วาระการพิจารณา # {selectedDiscussion.id}
                    </span>
                    <h3 className="font-bold text-slate-800 text-sm mt-0.5">{selectedDiscussion.title}</h3>
                  </div>
                  <button
                    onClick={() => setSelectedDiscussion(null)}
                    className="p-1 rounded-md text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <p className="text-xs text-slate-600 leading-relaxed bg-slate-50 p-3 rounded-xl">
                  {selectedDiscussion.description}
                </p>

                {/* Quick Vote Cast Action */}
                <div className="p-4 bg-gradient-to-tr from-blue-50 to-indigo-50/60 rounded-xl border border-blue-200/80 space-y-3 text-xs">
                  <span className="font-bold text-blue-950 flex items-center gap-1.5">
                    <Vote className="w-4 h-4 text-blue-700" /> ลงคะแนนมติของท่าน (กรรมการ / ผู้บริหาร):
                  </span>

                  <input
                    type="text"
                    placeholder="ความเห็นหรือเหตุผลประกอบการลงคะแนน (ทางเลือก)..."
                    value={voteComment}
                    onChange={(e) => setVoteComment(e.target.value)}
                    className="w-full p-2 bg-white border border-blue-200 rounded-lg text-xs"
                  />

                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => handleCastVote('approve')}
                      className="py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-xs flex items-center justify-center gap-1 shadow-xs transition-colors"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" /> เห็นชอบ
                    </button>
                    <button
                      onClick={() => handleCastVote('reject')}
                      className="py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg font-bold text-xs flex items-center justify-center gap-1 shadow-xs transition-colors"
                    >
                      <XCircle className="w-3.5 h-3.5" /> ไม่เห็นชอบ
                    </button>
                    <button
                      onClick={() => handleCastVote('abstain')}
                      className="py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg font-bold text-xs flex items-center justify-center gap-1 shadow-xs transition-colors"
                    >
                      งดออกเสียง
                    </button>
                  </div>
                </div>

                {/* Votes List */}
                <div className="space-y-2 text-xs">
                  <span className="font-bold text-slate-700 block">รายชื่อผู้ลงมติแล้ว ({selectedDiscussion.votes.length} ท่าน):</span>
                  <div className="space-y-1.5 max-h-36 overflow-y-auto">
                    {selectedDiscussion.votes.map((v, i) => (
                      <div key={i} className="p-2 rounded-lg bg-slate-50 flex items-center justify-between text-xs">
                        <div>
                          <span className="font-semibold text-slate-800">{v.voter_name}</span>
                          <span className="text-[10px] text-slate-500 block">{v.voter_role}</span>
                        </div>
                        {v.vote === 'approve' ? (
                          <span className="text-emerald-700 font-bold text-[11px] bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                            เห็นชอบ
                          </span>
                        ) : v.vote === 'reject' ? (
                          <span className="text-rose-700 font-bold text-[11px] bg-rose-50 px-2 py-0.5 rounded border border-rose-200">
                            ไม่เห็นชอบ
                          </span>
                        ) : (
                          <span className="text-slate-600 text-[11px]">งดออกเสียง</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Comment Thread */}
                <div className="space-y-3 pt-3 border-t border-slate-100 text-xs">
                  <span className="font-bold text-slate-700 flex items-center gap-1.5">
                    <MessageSquare className="w-4 h-4 text-blue-600" /> ข้อเสนอแนะ & การอภิปราย:
                  </span>

                  <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                    {selectedDiscussion.comments.map((comm) => (
                      <div key={comm.id} className="p-2.5 rounded-xl bg-slate-50 border border-slate-100 space-y-1">
                        <div className="flex justify-between items-center text-[10px]">
                          <span className="font-bold text-blue-900">{comm.author_name}</span>
                          <span className="text-slate-400">{comm.author_role}</span>
                        </div>
                        <p className="text-slate-700">{comm.content}</p>
                      </div>
                    ))}
                  </div>

                  <form onSubmit={handlePostComment} className="flex gap-2 pt-1">
                    <input
                      type="text"
                      placeholder="แสดงความคิดเห็นต่อวาระนี้..."
                      value={commentInput}
                      onChange={(e) => setCommentInput(e.target.value)}
                      className="flex-1 p-2 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                    />
                    <button
                      type="submit"
                      className="px-3 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-xs font-semibold"
                    >
                      <Send className="w-3.5 h-3.5" />
                    </button>
                  </form>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400 space-y-2">
                <Vote className="w-10 h-10 mx-auto opacity-30 text-blue-600" />
                <p className="text-xs font-medium">คลิกเลือกวาระการประชุมด้านซ้ายเพื่อดูรายละเอียดและร่วมลงมติ</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* New Proposal Modal */}
      {showNewProposalModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-slate-100 overflow-hidden my-6 animate-in fade-in zoom-in-95">
            <div className="p-5 bg-blue-900 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Vote className="w-5 h-5" />
                <h3 className="font-bold text-base">ยื่นญัตติ / วาระพิจารณาคณะกรรมการ</h3>
              </div>
              <button
                onClick={() => setShowNewProposalModal(false)}
                className="p-1 rounded-lg text-slate-300 hover:text-white"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateProposal} className="p-6 space-y-4 text-xs">
              <div>
                <label className="font-semibold text-slate-700 block mb-1">หัวข้อวาระ / ญัตติ *</label>
                <input
                  type="text"
                  required
                  placeholder="เช่น โครงการติดตั้งไฟโซลาร์เซลล์ส่องสว่างทางเดินสวนส่วนกลาง"
                  value={proposalTitle}
                  onChange={(e) => setProposalTitle(e.target.value)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">หมวดหมู่</label>
                  <select
                    value={proposalCategory}
                    onChange={(e) => setProposalCategory(e.target.value as any)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  >
                    <option value="security_rule">กฎระเบียบ & ความปลอดภัย</option>
                    <option value="maintenance_project">โครงการซ่อมบำรุง</option>
                    <option value="finance_budget">งบประมาณ & การเงิน</option>
                    <option value="procurement">จัดซื้อจัดจ้าง</option>
                    <option value="urgent_vote">วาระเร่งด่วน</option>
                  </select>
                </div>

                <div>
                  <label className="font-semibold text-slate-700 block mb-1">งบประมาณที่ขออนุมัติ (บาท)</label>
                  <input
                    type="number"
                    placeholder="เช่น 35000"
                    value={proposalBudget}
                    onChange={(e) => setProposalBudget(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
              </div>

              <div>
                <label className="font-semibold text-slate-700 block mb-1">กำหนดปิดการลงมติ</label>
                <input
                  type="date"
                  value={proposalDeadline}
                  onChange={(e) => setProposalDeadline(e.target.value)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                />
              </div>

              <div>
                <label className="font-semibold text-slate-700 block mb-1">รายละเอียดและเหตุผลความจำเป็น *</label>
                <textarea
                  rows={4}
                  required
                  placeholder="ระบุที่มา วัตถุประสงค์ ผลประโยชน์ต่อลูกบ้าน และขั้นตอนการดำเนินงาน..."
                  value={proposalDescription}
                  onChange={(e) => setProposalDescription(e.target.value)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowNewProposalModal(false)}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 font-medium"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl bg-blue-700 hover:bg-blue-800 text-white font-bold shadow-md shadow-blue-600/20"
                >
                  เปิดวาระลงมติ
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
